import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@quebon/ui/components/empty'
import { Spinner } from '@quebon/ui/components/spinner'
import { ToggleGroup, ToggleGroupItem } from '@quebon/ui/components/toggle-group'
import { cn } from '@quebon/ui/lib/utils'
import { getQuizCoverage, type QuizCoverageQuiz, type QuizCoveragePurposeGroup } from '@/api/quizCoverage'
import { InlineDebugId } from './InlineDebugId'
import { quizLocationLabel } from './quizLocation'
import { QuizPreviewPanel } from './QuizPreviewPanel'

/** quiz-mgt 스튜디오와 같은 라벨 (quiz-mgt/packages/ai-draft/src/pr-comment.ts). */
const PURPOSE_LABELS: Record<string, string> = {
  practice: '반복 연습',
  diagnose: '오개념 진단',
  thinking: '사고력',
  imagination: '상상력',
}
const NO_PURPOSE_KEY = '__none__'

const SYNCED_AT_FORMAT = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})

function purposeKey(purpose: string | null): string {
  return purpose ?? NO_PURPOSE_KEY
}

function purposeLabel(purpose: string | null): string {
  if (purpose === null) return '목적 없음'
  return PURPOSE_LABELS[purpose] ?? purpose
}

interface HubItemGroup {
  hubItemId: string
  misconceptions: string[]
  quizzes: QuizCoverageQuiz[]
}

/** 서버가 문항·인스턴스 순으로 정렬해 보내므로 순서를 유지한 채 문항별로 묶는다. */
function groupByHubItem(quizzes: QuizCoverageQuiz[]): HubItemGroup[] {
  const groups = new Map<string, HubItemGroup>()
  for (const quiz of quizzes) {
    const group = groups.get(quiz.hubItemId) ?? { hubItemId: quiz.hubItemId, misconceptions: [], quizzes: [] }
    group.quizzes.push(quiz)
    group.misconceptions = [...new Set([...group.misconceptions, ...quiz.misconceptions])].sort()
    groups.set(quiz.hubItemId, group)
  }
  return [...groups.values()]
}

export function QuizCoveragePanel({ lessonId }: { lessonId: number }) {
  const coverage = useQuery({
    queryKey: ['inspection', 'quizCoverage', lessonId],
    queryFn: () => getQuizCoverage(lessonId),
    staleTime: 5 * 60 * 1000,
  })
  const [selectedKey, setSelectedKey] = useState<string>('practice')
  // null이면 기본 상태(선택한 목적의 첫 문항만 펼침)
  const [openItemIds, setOpenItemIds] = useState<Set<string> | null>(null)

  const groups = coverage.data?.purposes ?? []
  const selectedGroup: QuizCoveragePurposeGroup | undefined =
    groups.find(group => purposeKey(group.purpose) === selectedKey) ?? groups[0]
  const items = useMemo(() => groupByHubItem(selectedGroup?.quizzes ?? []), [selectedGroup])
  const openIds = openItemIds ?? new Set(items.length > 0 ? [items[0].hubItemId] : [])
  const allOpen = items.length > 0 && items.every(item => openIds.has(item.hubItemId))

  const selectPurpose = (key: string) => {
    setSelectedKey(key)
    setOpenItemIds(null)
  }
  const toggleItem = (hubItemId: string) => {
    const next = new Set(openIds)
    if (next.has(hubItemId)) next.delete(hubItemId)
    else next.add(hubItemId)
    setOpenItemIds(next)
  }
  const toggleAll = () => {
    setOpenItemIds(allOpen ? new Set() : new Set(items.map(item => item.hubItemId)))
  }

  if (coverage.isPending) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground" role="status">
        <Spinner />
        문제 커버리지를 불러오는 중입니다
      </div>
    )
  }

  if (coverage.isError) {
    return (
      <div className="px-6 py-6">
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>문제 커버리지를 불러오지 못했습니다.</span>
            <Button variant="outline" size="sm" onClick={() => void coverage.refetch()}>다시 시도</Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const data = coverage.data
  const nonHubNotice = data.nonHubQuizCount > 0
    ? `허브 메타가 없는 문제 ${data.nonHubQuizCount}개는 문제 탭에서 확인합니다.`
    : null

  if (data.hubQuizCount === 0) {
    return (
      <div className="px-6 py-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>이 레슨에 허브(qmgt) 문제가 없습니다</EmptyTitle>
            {nonHubNotice && <EmptyDescription>{nonHubNotice}</EmptyDescription>}
          </EmptyHeader>
        </Empty>
      </div>
    )
  }

  return (
    <div>
      <div className="sticky top-0 z-30 flex flex-col gap-3 border-b border-border bg-background/95 px-6 pb-3 pt-6 backdrop-blur">
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>허브 문제 <strong className="font-semibold text-foreground tabular-nums">{data.hubQuizCount}</strong></span>
          <span>허브 문항 <strong className="font-semibold text-foreground tabular-nums">{data.hubItemCount}</strong></span>
          {data.lastSyncedAt && <span>마지막 동기화 {SYNCED_AT_FORMAT.format(new Date(data.lastSyncedAt))}</span>}
          {data.excludedDeletedCount > 0 && <span>삭제되어 제외 {data.excludedDeletedCount}개</span>}
          {nonHubNotice && <span>{nonHubNotice}</span>}
        </p>

        <ToggleGroup
          value={[purposeKey(selectedGroup?.purpose ?? null)]}
          onValueChange={values => { if (values[0]) selectPurpose(values[0]) }}
          aria-label="문항 목적"
          variant="outline"
          className="w-full"
        >
          {groups.map(group => {
            const ratio = data.hubQuizCount > 0 ? (group.quizCount / data.hubQuizCount) * 100 : 0
            return (
              <ToggleGroupItem
                key={purposeKey(group.purpose)}
                value={purposeKey(group.purpose)}
                className="h-auto min-w-0 flex-1 flex-col items-start gap-1 px-3 py-2.5 text-left"
              >
                <span>{purposeLabel(group.purpose)}</span>
                <span className="text-xl font-semibold text-foreground tabular-nums">
                  {group.quizCount}
                  <span className="ml-1 text-xs font-normal text-muted-foreground">문제 · 문항 {group.itemCount}</span>
                </span>
                <span className="block h-1 w-full overflow-hidden rounded-full bg-border" aria-hidden="true">
                  <span className="block h-full bg-primary" style={{ width: `${ratio}%` }} />
                </span>
              </ToggleGroupItem>
            )
          })}
        </ToggleGroup>

        {items.length > 0 && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{purposeLabel(selectedGroup?.purpose ?? null)} · 문항 {items.length}개</span>
            <Button variant="ghost" size="sm" onClick={toggleAll}>{allOpen ? '모두 접기' : '모두 펼치기'}</Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 px-6 py-4">
        {items.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>이 목적의 허브 문제가 없습니다</EmptyTitle>
            </EmptyHeader>
          </Empty>
        ) : items.map(item => {
          const open = openIds.has(item.hubItemId)
          return (
            <section key={item.hubItemId} className="overflow-hidden rounded-lg border border-border bg-card">
              <div className="flex flex-wrap items-center gap-2 px-2 py-1.5">
                <Button variant="ghost" aria-expanded={open} onClick={() => toggleItem(item.hubItemId)} className="justify-start">
                  <ChevronRight className={cn('transition-transform', open && 'rotate-90')} aria-hidden="true" />
                  <span className="font-semibold">문항 {item.hubItemId}</span>
                  <span className="text-muted-foreground">문제 {item.quizzes.length}개</span>
                </Button>
                {item.misconceptions.length > 0 && (
                  <div className="ml-auto flex flex-wrap justify-end gap-1" aria-label="오개념">
                    {item.misconceptions.map(misconception => (
                      <Badge key={misconception} variant="outline">{misconception}</Badge>
                    ))}
                  </div>
                )}
              </div>
              {open && (
                <div className="flex flex-col gap-2 border-t border-border bg-background px-3 py-3">
                  {item.quizzes.map((quiz, index) => (
                    <article key={quiz.quizId} className="rounded-md border border-border bg-card p-3">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-muted-foreground tabular-nums">{index + 1}.</span>
                        <InlineDebugId quizId={quiz.quizId} />
                        {quiz.locations.map(location => (
                          <Badge key={location} variant="secondary">{quizLocationLabel(location)}</Badge>
                        ))}
                        <span className="text-muted-foreground">인스턴스 #{quiz.hubK}</span>
                      </div>
                      <div className="mt-2">
                        <QuizPreviewPanel quizId={quiz.quizId} />
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )
        })}
      </div>
    </div>
  )
}
